"use client";

import React, { useEffect } from "react";
import { Formik, Form } from "formik";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  insertPriceRange,
  updatePriceRange,
  findAllPriceRange,
  findByIDPriceRange,
} from "./priceRange.service";
import { priceRangeValidationSchema } from "./priceRange.validation";
import { clearPriceRangeDetails } from "./priceRange.slice";
import { closed } from "@/redux/slices/drawerSlice";
import { STATUS_OPTIONS } from "@/lib/constants";
import { InputField } from "@/components/common/FormFields/InputField";
import { SelectField } from "@/components/common/FormFields/SelectField";
import { CustomButton } from "@/components/common/CustomButton";
import { Save, X } from "lucide-react";
import { FormLoader } from "@/components/common/FormLoader";

const PriceRangeForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const { PriceRangeDetail, formLoading, loading } = useAppSelector(
    (state) => state.priceRange,
  );
  const { data: PriceRangeID } = useAppSelector((state) => state.drawer);

  useEffect(() => {
    if (PriceRangeID) {
      dispatch(findByIDPriceRange(PriceRangeID));
    }
  }, [PriceRangeID, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearPriceRangeDetails());
    };
  }, [dispatch]);

  const handleSubmit = async (values: any) => {
    const payload: any = {
      ItemQty: values?.ItemQty ? +values?.ItemQty : 0,
      ItemPercentage: values?.ItemPercentage ? +values?.ItemPercentage : 0,
      IsActive: values?.IsActive ?? true,
    };

    if (PriceRangeID) {
      payload.ID = PriceRangeID;
      await dispatch(updatePriceRange(payload));
    } else {
      await dispatch(insertPriceRange(payload));
    }

    dispatch(findAllPriceRange());
    dispatch(closed());
  };

  return (
    <div className="relative">
      <FormLoader loading={formLoading || loading} />
      <Formik
        initialValues={{
          ItemQty: PriceRangeDetail?.ItemQty ?? "",
          ItemPercentage: PriceRangeDetail?.ItemPercentage ?? "",
          IsActive: PriceRangeDetail?.IsActive ?? true,
        }}
        validationSchema={priceRangeValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {() => (
          <Form className="flex flex-col gap-6">
            <div className="space-y-4">
              <InputField
                label="Item Quantity"
                name="ItemQty"
                type="number"
                placeholder="Enter Item Quantity"
                required
              />

              <InputField
                label="Item Percentage (%)"
                name="ItemPercentage"
                type="number"
                max={100}
                placeholder="Enter Item Percentage"
                required
              />

              <SelectField
                label="Status"
                name="IsActive"
                options={STATUS_OPTIONS}
                required
              />
            </div>

            <div className="flex items-center gap-3 pt-6 mt-6 border-t border-slate-100">
              <CustomButton
                type="submit"
                variant="primary"
                loading={formLoading}
                icon={<Save size={14} />}
                fullWidth
              >
                {PriceRangeID ? "Update" : "Insert"}
              </CustomButton>
              <CustomButton
                type="button"
                variant="outline"
                icon={<X size={14} />}
                onClick={() => dispatch(closed())}
                disabled={formLoading}
                fullWidth
              >
                Cancel
              </CustomButton>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default PriceRangeForm;
